<script setup lang="ts">
/**
 * A content tile: one recommended article, video or audio, drawn as its own image (IPD-2978 12.1).
 *
 * The bento holds two of these side by side. They are the distilled replacement for the old
 * endless content bands, so a tile shows one item and nothing else — no carousel, no overflow.
 *
 * Presentational only. Which item lands here, in what order and for how long is entirely the
 * backend's (IPD-2969): the pair refreshes together every three days, and a read item keeps its
 * slot until it does. Nothing here re-derives any of that.
 */
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import MediaPlayBadge from '@/vue/atoms/media-play-badge.vue';
import ConsumedMarker from '@/vue/atoms/consumed-marker.vue';
import CompanyBadge from '@/vue/atoms/company-badge.vue';
import type {TodayContent} from '@/ts/types/component/today-page';
import {LibraryMediaTypeDTO} from '@/ts/types/dto/recco/library.dto';
import {isPlayableMediaType, mediaLabelWithDuration, statusMarkerLabelKey} from '@/ts/utils/recco-label-keys';

const props = defineProps<{
  content: TodayContent;
}>();

const emit = defineEmits<{
  /**
   * The item was opened by a primary click, navigation included, so the page can report it
   * without having to distinguish how the card was reached. The card navigates itself either way.
   *
   * Not every way of opening it: a middle-click fires `auxclick`, so opening the piece in a new
   * tab navigates without emitting. IPD-2974 needs to know that before it counts opens from this.
   */
  select: [content: TodayContent];
}>();

const {t: $t} = useI18n();

/**
 * A picture the CMS points at but the media host cannot serve.
 *
 * `skip-loader` renders a bare `<img>`, which paints the browser's own broken-image glyph across
 * the whole card when the request fails — not the dark surface the card is documented to fall back
 * on. Dropping the element on `error` is what makes that fallback real. Reset per piece, because
 * the same component instance is handed a different one when the pair refreshes (and, in
 * development, on every flick of the variant switcher).
 */
const imageFailed = ref(false);

watch(() => props.content.imageUrl, () => {
  imageFailed.value = false;
});

// The image is a background the copy sits on rather than a photograph to study, so it is asked
// for at the widest the tile realistically gets rather than at the widest it can: three to a row
// on a full bento that is ~460px each, but the middle band stretches the odd third tile across
// the whole row (the grid's no-holes rule), which is where this figure comes from. lazy-image
// scales it by the device pixel ratio on top.
const IMAGE_WIDTH_PX = 640;

/**
 * The eyebrow names the medium as a noun, which the app has nowhere else: everywhere it labels
 * content today it uses the verb the user is about to perform ("Read", "Watch", "Listen"), and
 * the design draws the kind of thing instead. Kept local for the same reason the media-type map
 * in `recco-media-type-and-duration.vue` is: three entries, one consumer.
 */
const MEDIA_LABEL_KEY: Record<LibraryMediaTypeDTO, string> = {
  [LibraryMediaTypeDTO.Read]: 'today_content_media_article',
  [LibraryMediaTypeDTO.Watch]: 'today_content_media_video',
  [LibraryMediaTypeDTO.Listen]: 'today_content_media_audio',
};

// No fallback, and none needed: the map is a total Record over the enum, and the only thing that
// produces the value is `contentMediaType`, which returns one of its three members. The read
// marker below indexes its own map the same way, so both lookups stand or fall together rather
// than one carrying a guard the other lacks.
// "Audio · 12 min" (IPD-3375): the length rides behind the medium, and because the accessible
// name below is built from this same string, assistive technology hears it too.
const mediaLabel = computed<string>(() => mediaLabelWithDuration($t(MEDIA_LABEL_KEY[props.content.media]), props.content.duration, $t));

const playable = computed<boolean>(() => isPlayableMediaType(props.content.media));

/**
 * Read is conveyed by dimming alone, which no assistive technology can report, so it is carried
 * in the name instead — through the same Watched / Read / Listened wording Explore already marks
 * consumed content with, rather than a second vocabulary for the same fact.
 *
 * Appended after the visible lines, never between them: WCAG 2.5.3 asks the name to contain the
 * visible text in order, or voice control cannot activate the card by reading it aloud. Nothing
 * here enforces that. axe's label-content-name-mismatch is experimental, so a default run (which
 * is what checkA11y does) leaves it out of violations, passes and inapplicable alike; and were it
 * enabled it would flag this markup anyway, since Vue's condense drops the whitespace between the
 * two spans below, leaving axe with "VideoWhat is Balm?" to compare by substring after stripping
 * punctuation. The guideline itself is met — every visible word is in the name, in order — but it
 * is met by this ordering, not by a check that would catch its loss.
 */
const ariaLabel = computed<string>(() => {
  const parts = [mediaLabel.value, props.content.title];
  if (props.content.read) {
    parts.push($t(statusMarkerLabelKey[props.content.media]));
  }
  // Blank parts are dropped rather than joined: the CMS lets a content block have no name at
  // all, and an empty one would otherwise leave the name reading "Article, , Watched".
  return parts.filter(Boolean).join(', ');
});
</script>

<template>
  <router-link
    :to="content.to"
    class="content-tile"
    :class="{'content-tile--read': content.read}"
    :aria-label="ariaLabel"
    @click="emit('select', content)"
  >
    <lazy-image
      v-if="content.imageUrl && !imageFailed"
      :src="content.imageUrl"
      :width="IMAGE_WIDTH_PX"
      skip-loader
      alt=""
      class="content-tile__image"
      @error="imageFailed = true"
    />
    <span class="content-tile__scrim" aria-hidden="true"/>

    <media-play-badge v-if="playable"/>
    <!-- Partner attribution, same corner and same component as every other content surface. -->
    <company-badge :company-image="content.companyImage"/>

    <span class="content-tile__copy">
      <!-- The same marker Explore puts on content it has already served, so an item met in both
           places is described the same way in both. Explore hangs it in the picture's bottom-left
           corner, which on this card is where the copy sits, so it rides above the copy instead:
           the card anchors its content to the bottom, so this grows the block upward and the two
           lines below do not move. It is aria-hidden, so the state reaches assistive technology
           through the card's name above and is not announced twice. -->
      <consumed-marker v-if="content.read" :media-type="content.media"/>
      <span class="content-tile__eyebrow">{{ mediaLabel }}</span>
      <span class="content-tile__title">{{ content.title }}</span>
    </span>
  </router-link>
</template>

<style scoped lang="scss">
// The card is the tile: the image bleeds to all four edges and the copy sits on it, so there is
// no surface of the page's own showing anywhere. The dark pair underneath is what a card with no
// image, or one whose image has not loaded yet, falls back to — never the themed tile surface,
// which would leave the fixed light copy on a light card.
//
// The design draws these at a 24px radius against the 16px of the tiles above; unified to the
// bento's own radius rather than carrying a third value for one row (raised with design).
.content-tile {
  @include focus-visible;

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  // The design sets the copy 24px in from the sides and 32px up from the floor, which is exactly
  // where these two steps of the scale land on desktop, and keeps the same proportion below it.
  padding: var(--Spacing-spacing-4) var(--Spacing-spacing-4) var(--Spacing-spacing-5);
  border-radius: var(--today-radius-section);
  background: var(--today-surface-dark);
  color: var(--today-ink-on-dark);
  text-decoration: none;
  overflow: hidden;
}

.content-tile__image {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

// Read content is not replaced, it recedes: the image fades back towards the card's own dark
// surface while the copy stays at full strength. Dimming the copy instead is what the rules doc
// asks for in words, but it is the one direction that cannot be done safely here — the copy is
// already the only thing holding contrast over an arbitrary CMS photograph.
.content-tile--read .content-tile__image {
  opacity: 0.6;
}

// The image comes from the CMS and can be anything, including a near-white photograph, so the copy
// over it carries a shade of its own. It is a band under the copy rather than a wash over the whole
// picture: a flat wash at the value the design drew buys nothing for legibility — the copy still
// sits at 1.48:1 over a bright photograph — while costing the picture everywhere.
//
// THE WEIGHTS ARE NOT A LOOK, they are the contrast floor (see `today-copy-scrim`). Measured over a
// white photograph, at the top edge of each line, where the shade is thinnest: the worst position is
// 4.78:1 at the widest breakpoint with a two-line title, and 4.73:1 with the reader's minimum font
// size raised to 20. The floor is 4.5:1 for BOTH — the 10px eyebrow, and the title too, because
// `--today-text-lead` resolves to 18px at every breakpoint and 18px bold is 13.5pt, under WCAG's
// 14pt-bold threshold for large text. Only at `min-font-size-20` does the title become large text.
//
// It holds 68% to 130px because that is past the two-line title AND its eyebrow at every breakpoint
// and font size; a straight ramp from the edge would fail the eyebrow, which is the smallest copy
// and the furthest from the shade.
//
// This costs LESS picture than the flat-veil version it replaces, not more: there is no flat layer
// at all now, so the top of the card is untouched where it used to be veiled at 25% everywhere.
.content-tile__scrim {
  position: absolute;
  inset: 0;
  z-index: 0;

  @include today-copy-scrim(to top, 72% 0, 68% 130px, 30% 170px, 0 230px);
}

// Both overlays are shared atoms, so only where they sit is set here: they line up with the copy
// below instead of the atoms' tighter default, which is what the design draws.
.content-tile :deep(.media-play-badge) {
  top: var(--Spacing-spacing-4);
  left: var(--Spacing-spacing-4);
}

// The atom hangs itself in a picture's corner, which works where the copy sits below the picture.
// Here it rides in the copy's own column instead, so it needs to leave the corner and join the
// flow. Nothing else about it changes: same chip, same wording, same component as Explore.
.content-tile :deep(.consumed-marker) {
  position: static;
  align-self: flex-start;
  margin-bottom: var(--Spacing-spacing-2);
}

.content-tile__copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);
  min-width: 0;
}

.content-tile__eyebrow {
  @include font-caption-sm;

  font-size: var(--today-text-eyebrow);
}

// Titles come from the CMS and can be long. Two lines, then an ellipsis: one line truncates
// titles the design shows in full, and an unbounded title would push the card's copy up over the
// part of the scrim that is not dark enough to carry it.
.content-tile__title {
  @include font-body-bold;

  display: -webkit-box;
  font-size: var(--today-text-lead);
  overflow: hidden;
  // German compounds from the CMS can be longer than the card is wide, and a word with no break
  // opportunity would be clipped by the overflow above rather than wrapped into the second line
  // the clamp allows.
  overflow-wrap: anywhere;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
